import { useState } from 'react';
import { Lock } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { TermsModal } from '@/components/common/TermsModal';
import { getApiErrorCode } from '@/lib/utils/error.utils';
import { useSharedProfileStatus } from '@/features/shared-profile/hooks/useSharedProfileStatus';
import { useSendSharedProfileOtp } from '@/features/shared-profile/hooks/useSendSharedProfileOtp';
import { SharedProfileIdentityHeader } from '@/features/shared-profile/components/SharedProfileIdentityHeader';
import { SharedProfileExpiredState } from '@/features/shared-profile/components/SharedProfileExpiredState';

const TERMINAL_STATUSES = new Set(['expired', 'revoked']);

/**
 * Entry point for a Shared Startup Profile link (MYN-58 screens 1 & 2):
 * checks the link's status first (so an already-expired/revoked/unknown
 * token shows the link-expired screen instead of a verify prompt it could
 * never pass), then the email-verify prompt for a live link.
 */
export function SharedProfileGatePage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const S = UI_STRINGS.SHARED_PROFILE;

  const status = useSharedProfileStatus(token);
  const sendOtp = useSendSharedProfileOtp(token);
  const [termsOpen, setTermsOpen] = useState(false);

  if (status.isLoading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const notFound = status.isError && getApiErrorCode(status.error) === API_ERROR_CODES.SHARE_NOT_FOUND;
  const terminal = status.isError || (status.data && TERMINAL_STATUSES.has(status.data.status));
  if (notFound || terminal || !status.data) {
    return <SharedProfileExpiredState />;
  }

  const { recipientEmail, startupCompanyName } = status.data;

  const handleSendCode = () => {
    sendOtp.mutate(undefined, {
      onSuccess: () =>
        navigate(ROUTES.SHARED_PROFILE_VERIFY_OTP.replace(':token', token), {
          state: { email: recipientEmail, companyName: startupCompanyName },
        }),
    });
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="flex w-full max-w-130 flex-col items-center gap-8 rounded-card bg-white p-12 shadow-card">
        <SharedProfileIdentityHeader
          companyName={startupCompanyName}
          badge={
            <span className="font-sans text-xs font-semibold uppercase tracking-wide text-green">
              {S.SECURE_ACCESS_BADGE}
            </span>
          }
        />

        <div className="h-px w-full bg-input-border" />

        <div className="flex w-full flex-col items-center gap-4 text-center">
          <h2 className="font-sans text-2xl font-bold text-navy">{S.VERIFY_HEADING}</h2>
          <p className="text-sm text-subtext">{S.VERIFY_SUBTITLE}</p>

          {sendOtp.isError && (
            <p role="alert" className="text-sm text-danger">
              {S.SEND_CODE_ERROR}
            </p>
          )}

          <div className="w-full text-left">
            <Input label="Recipient email" value={recipientEmail} locked rightIcon={<Lock size={16} />} />
            <p className="mt-1.5 pl-1 text-xs text-slate">{S.EMAIL_LOCKED_HINT}</p>
          </div>

          <Button
            type="button"
            variant="green"
            disabled={sendOtp.isPending}
            className="w-full py-3.5 text-base font-semibold"
            onClick={handleSendCode}
          >
            {sendOtp.isPending ? S.SENDING_CODE : S.SEND_CODE}
          </Button>

          <p className="text-[11px] text-slate">
            {S.TERMS_AGREEMENT_PREFIX}
            <button
              type="button"
              onClick={() => setTermsOpen(true)}
              className="cursor-pointer underline hover:text-navy"
            >
              {S.TERMS_AGREEMENT_LINK}
            </button>
            {S.TERMS_AGREEMENT_SUFFIX}
          </p>
        </div>
      </div>

      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}
